'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactMethod {
  icon: string;
  title: string;
  details: string[];
  action: string;
  available: string;
}

const ContactInfo = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const contactMethods: ContactMethod[] = [
    {
      icon: 'PhoneIcon',
      title: 'Call Us',
      details: ['+91 98862 76722'],
      action: 'Mon-Sat: 9:00 AM - 7:00 PM',
      available: 'Available for immediate queries'
    },
    {
      icon: 'EnvelopeIcon',
      title: 'Email Us',
      details: ['hello@hayatinteriors.com'],
      action: 'Response within 24 hours',
      available: ''
    },

    {
      icon: 'VideoCameraIcon',
      title: 'Video Consultation',
      details: ['Book a virtual meeting'],
      action: 'Perfect for NRI clients',
      available: 'Schedule at your convenience'
    }
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '9:00 AM - 7:00 PM' },
    { day: 'Saturday', hours: '10:00 AM - 6:00 PM' },
    { day: 'Sunday', hours: 'Closed (Emergency: +91 98862 76722)' }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: 'CameraIcon', url: 'https://www.instagram.com/hayatinteriors.co/', handle: '@hayatinteriors.co' },
    { name: 'Threads', icon: 'LinkIcon', url: 'https://www.threads.com/@hayatinteriors.co', handle: '@hayatinteriors.co' },
    { name: 'Facebook', icon: 'UserGroupIcon', url: 'https://www.facebook.com/profile.php?id=61584330734970', handle: 'Hayat Interiors' },
    { name: 'LinkedIn', icon: 'BriefcaseIcon', url: 'https://www.linkedin.com/company/hayat-interiors/?viewAsMember=true', handle: 'Hayat Interiors' }
  ];

  if (!isHydrated) {
    return (
      <div className="space-y-8">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <div className="h-8 bg-white/10 rounded w-1/2 mb-6 animate-pulse" />
          <div className="space-y-4">
            <div className="h-20 bg-white/10 rounded animate-pulse" />
            <div className="h-20 bg-white/10 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h2 className="font-headline text-2xl font-bold text-white mb-6">Get In Touch</h2>
        
        <div className="space-y-6">
          {contactMethods.map((method, index) => (
            <div key={index} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-white/5 transition-colors">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name={method.icon as any} size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-body font-semibold text-white mb-1">{method.title}</h3>
                {method.details.map((detail, idx) => {
                  const isEmail = detail.includes('@');
                  const isPhone = /\+?\d[\d\s-]{6,}\d/.test(detail);
                  if (isEmail) {
                    return (
                      <p key={idx} className="font-body text-sm text-white/70">
                        <a href={`mailto:${detail}`} className="text-white/90 hover:underline">{detail}</a>
                      </p>
                    );
                  }
                  if (isPhone) {
                    // normalize phone for tel: (remove spaces)
                    const tel = detail.replace(/[^+\d]/g, '');
                    return (
                      <p key={idx} className="font-body text-sm text-white/70">
                        <a href={`tel:${tel}`} className="text-white/90 hover:underline">{detail}</a>
                      </p>
                    );
                  }
                  return (
                    <p key={idx} className="font-body text-sm text-white/70">{detail}</p>
                  );
                })}
                <p className="font-body text-xs text-white/90 mt-2">{method.action}</p>
                <p className="font-body text-xs text-white/60">{method.available}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h2 className="font-headline text-2xl font-bold text-white mb-6">Visit Our Studio</h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Icon name="MapPinIcon" size={20} className="text-white mt-1 flex-shrink-0" />
            <div>
              <p className="font-body text-sm text-white font-medium">Hayat Interiors Design Studio</p>
              <p className="font-body text-sm text-white/70">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=First+Floor%2C+513%2FC%2C+4th+Block%2C+above+Nilambur+Furniture%2C+1st+Stage%2C+Telecom+Layout%2C+HBR+Layout%2C+Bengaluru+560043"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:underline"
                >
                  First Floor, 513/C, 4th Block, above Nilambur Furniture,<br />
                  1st Stage, Telecom Layout, HBR Layout, Bengaluru 560043<br />
                  India
                </a>
              </p>
            </div>
          </div>
          
          <div className="mt-6 h-64 rounded-lg overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Hayat Interiors Studio Location"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=12.9716,77.5946&z=14&output=embed"
              className="border-0"
            />
          </div>
        </div>
      </div>

    </div>
  );
};

export default ContactInfo;